import type { AnswerSuggestion, DocumentSummary } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob } from '../../lib/api';
import { useSession } from '../../lib/session';
import { documentState } from './files';

/** Documents and the answers read from them (tasks 32–35), for the upload and review screens (task 40). */

const keys = {
  assessment: (t: string | undefined, id: string) => ['assessments', t, id] as const,
  documents: (t: string | undefined, id: string) => ['assessments', t, id, 'documents'] as const,
  suggestions: (t: string | undefined, id: string) => ['assessments', t, id, 'suggestions'] as const,
};

/** Sorting and reading happen in the background, so the list checks back while any document is pending. */
const POLL_MS = 3000;

export function useDocuments(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: keys.documents(tenant?.id, id),
    queryFn: () => api<DocumentSummary[]>('GET', `/assessments/${id}/documents`),
    enabled: !!tenant && enabled,
    refetchInterval: (q) => (q.state.data?.some((d) => documentState(d).pending) ? POLL_MS : false),
  });
}

export function useSuggestions(id: string, enabled = true) {
  const { tenant } = useSession();
  const docs = useDocuments(id, enabled);
  const reading = !!docs.data?.some((d) => documentState(d).pending);
  return useQuery({
    queryKey: keys.suggestions(tenant?.id, id),
    queryFn: () => api<AnswerSuggestion[]>('GET', `/assessments/${id}/suggestions`),
    enabled: !!tenant && enabled,
    // Suggestions appear a moment after a document is read.
    refetchInterval: reading ? POLL_MS : false,
  });
}

export interface Uploaded {
  duplicate: boolean;
  document: DocumentSummary;
}

/** One file, optionally for a question. The core scans it before storing; a refused file is an error. */
export function useUpload(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({ file, question }: { file: File; question?: string }) => {
      const form = new FormData();
      if (question) form.append('question', question);
      form.append('file', file, file.name);
      return api<Uploaded>('POST', `/assessments/${id}/documents`, form);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.documents(tenant?.id, id) }),
  });
}

/**
 * Accept or decline what a document says. Accepting passes the answer the person saw (`based_on`), so an
 * answer given since isn't overwritten (409). Answers, score and suggestions all reload afterwards.
 */
export function useDecide(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({ s, to }: { s: AnswerSuggestion; to: 'accept' | 'decline' }) =>
      api<unknown>(
        'POST',
        `/assessments/${id}/suggestions/${s.id}/${to}`,
        to === 'accept' ? { based_on: s.current?.response_id ?? null } : undefined,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.assessment(tenant?.id, id) }),
  });
}

/** Accepts several suggestions in one batch (task 106); any that no longer qualify come back as `left`. */
export function useAcceptTogether(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ accepted: string[]; left: string[] }>('POST', `/assessments/${id}/suggestions/accept`, { ids }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.assessment(tenant?.id, id) }),
  });
}

/** Saves a stored document through the browser's download, never opening it in the app. */
export async function downloadDocument(d: Pick<DocumentSummary, 'id' | 'file_name'>) {
  const blob = await apiBlob(`/documents/${d.id}/content`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = d.file_name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
